<template>
  <div class="city-input dadata-field" v-click-outside="handleClickOutside">
    <input
      class="city-input__input"
      :class="{
        'city-input__input--error': error,
        'city-input__input--valid': valid,
        'city-input__input--disabled': disabled,
      }"
      type="text"
      :id="id"
      :placeholder="placeholder"
      :value="inputValue"
      @input="handleNativeInput"
      @focus="handleFocus"
      :disabled="disabled"
    />
    <transition name="fade">
      <div v-if="isOpened" class="city-input__dropdown">
        <div v-if="isPending" class="city-input__text city-input__text--secondary city-input__title  city-input__loader">
          Поиск...
        </div>

        <template v-else>
          <template v-if="inputValue.length">
            <ul v-if="options.length" class="city-input__list">
              <li v-for="option in options" class="city-input__list-item" @click="handleItemClick(option)">
                <span class="city-input__text">г. {{ option.city }}</span>
                <span> {{ option.country }}, {{ option.city }}, </span>
                <span>
                  <!-- {{ option.district && "федеральный округ," }} -->
                  {{ option.region_with_type && `Регион (область): ${option.region_with_type}` }},
                </span>
                <span>
                  {{ option.index && `Индекс: ${option.index}` }}
                </span>
              </li>
            </ul>

            <div v-else class="city-input__text city-input__title city-input__text--secondary">
              Ничего не найдено
            </div>
          </template>

          <div class="city-input__text city-input__title city-input__text--secondary" v-else>
            Пример: г Москва
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { fetchCitySuggestions } from '@shared/services/dadata/dadata.service'
import { isObject } from '@shared/utils/type'

export default {
  name: 'CityInput',
  props: {
    token: {
      type: String,
      required: true,
    },
    value: {
      type: [Object, String],
      default: null,
    },
    id: {
      type: String,
      default: 'city-input',
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    valid: {
      type: Boolean,
      default: false,
    },
    minSearchLength: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      isOpened: false,
      inputValue: '',
      options: [],
      isPending: false,
      requestId: 0,
      debouncedSearch: null,
      isResolvingInitialValue: false,
      lastResolvedStringValue: '',
      isInternalValueUpdate: false,
    }
  },
  created() {
    this.debouncedSearch = debounce(this.searchOptions, 500)
  },
  beforeDestroy() {
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel()
    }
  },
  methods: {
    emitValue(value) {
      this.isInternalValueUpdate = true
      this.$emit('update:value', value)

      this.$nextTick(() => {
        this.isInternalValueUpdate = false
      })
    },
    formatValue(value) {
      if (!value) return ''

      if (typeof value === 'string') {
        return value
      }

      return value.city || ''
    },
    async fetchOptions(query) {
      return fetchCitySuggestions({
        query,
        token: this.token,
        locations: this.locations,
      })
    },
    async syncInputValueFromValue(value) {
      if (!value) {
        this.inputValue = ''
        this.options = []
        this.lastResolvedStringValue = ''
        return
      }

      if (typeof value === 'string') {
        this.inputValue = value
        await this.resolveInitialStringValue(value)
        return
      }

      this.inputValue = this.formatValue(value)
      this.options = [value]
    },
    async resolveInitialStringValue(value) {
      const query = String(value || '').trim()

      if (!query) return
      if (this.lastResolvedStringValue === query) return

      this.lastResolvedStringValue = query
      this.isResolvingInitialValue = true
      this.isPending = true

      try {
        const options = await this.fetchOptions(query)
        const option = options[0] || null

        if (!option) {
          this.inputValue = query
          this.options = []
          return
        }

        this.inputValue = this.formatValue(option)
        this.options = [option]

        this.emitValue(option)
      } catch (error) {
        console.error(error)

        this.inputValue = query
        this.options = []
      } finally {
        this.isPending = false
        this.isResolvingInitialValue = false
      }
    },
    handleFocus(event) {
      this.isOpened = true
      this.$emit('focus', event)
    },
    handleNativeInput(event) {
      this.inputValue = event.target.value

      const query = String(this.inputValue || '').trim()

      if (isObject(this.value)) {
        this.emitValue(null)
      }

      if (!query) {
        this.options = []
        this.isPending = false
        return
      }

      if (query.length < this.minSearchLength) {
        this.options = []
        this.isPending = false
        return
      }

      this.isPending = true
      this.debouncedSearch()
    },
    async searchOptions() {
      if (this.isResolvingInitialValue) return

      const query = String(this.inputValue || '').trim()

      if (!query || query.length < this.minSearchLength) {
        this.options = []
        this.isPending = false
        return
      }

      const currentRequestId = ++this.requestId

      try {
        const options = await this.fetchOptions(query)

        if (currentRequestId !== this.requestId) return

        this.options = options
      } catch (error) {
        if (currentRequestId !== this.requestId) return

        console.error(error)
        this.options = []
      } finally {
        if (currentRequestId === this.requestId) {
          this.isPending = false
        }
      }
    },
    resetField() {
      this.inputValue = ''
      this.options = []
      this.lastResolvedStringValue = ''
      this.emitValue(null)
    },
    setCurrentOption(option) {
      if (!option) {
        this.resetField()
        return
      }

      this.inputValue = this.formatValue(option)
      this.options = [option]

      this.emitValue(option)
    },
    handleClickOutside() {
      if (!this.isOpened) return

      this.setCurrentOption(this.value)
      this.isOpened = false
    },
    handleItemClick(option) {
      this.setCurrentOption(option)
      this.isOpened = false
    },
  },
  watch: {
    value: {
      immediate: true,
      async handler(value) {
        if (this.isInternalValueUpdate) {
          return
        }

        await this.syncInputValueFromValue(value)
      },
    },
    isOpened(value) {
      if (value) return

      this.isPending = false
    },
  },
}
</script>

<style lang="sass">
@import "~@src/legacy/sass/params"

.city-input
  position: relative
  list-style-type: none

  &__input
    width: 100%
    min-height: 48px
    padding: 0 16px
    border: var(--field-border-width, 1px) solid var(--field-border-color, #C8C8C8)
    border-radius: 4px

    &--error
      border: var(--field-error-border-width, 1px) solid var(--field-error-border-color, $error)

    &--disabled
      opacity: var(--field-opacity-disabled, 0.7)
      pointer-events: none
      background-color: var(--field-disabled-bg-color, #ededed)

    &::placeholder
      color: var(--field-placeholder-color, $gray-text)

  &__dropdown
    position: absolute
    background: #ffffff
    width: 100%
    border-radius: 4px
    max-height: 200px
    scrollbar-width: none
    overflow-y: scroll
    top: calc(100% + 4px)
    padding-block: 12px
    border: 1px solid var(--field-border-color, #C8C8C8)
    box-shadow: 0 4px 12px 0 #0000001A
    z-index: 1

  &__list
    color: #2c2c2c

  &__text
    font-family: Proxima Nova Semi, sans-serif

  &__text--secondary
    color: #a6a6a6

  &__title
    padding: 4px 24px

  &__list-item
    cursor: pointer
    padding: 12px 24px
    transition: 200ms
    transition-property: background
    display: flex
    flex-direction: column

    &:hover
      background: rgba(0, 0, 0, 0.1)

  &__loader
    animation-name: loading-opacity
    animation-duration: 1000ms
    animation-iteration-count: infinite
    animation-timing-function: ease-in-out
    opacity: 1

  &__placeholder
    padding: 12px 24px

@keyframes loading-opacity
  0%
    opacity: 1

  50%
    opacity: 0.2

  100%
    opacity: 1
</style>



// WEBPACK FOOTER //
// src/modules/shared/ui/public/forms/fields/CityInput.vue